<template>
  <div
    :class="[
      `mc-control-tower__item mc-control-tower__item--${convertColor(item)}`,
      { hide: !isExpanded },
    ]"
    @click="showDetail(item)"
  >
    <h4 class="mc-control-tower__title">
      <span :title="item.licensePlate">{{ item.licensePlate }} </span>

      <i
        title="Jadikan Favorit"
        :class="`mc-fa icon-${item.isPinned ? 'fill' : 'outline'}_star`"
        @click="($event) => setPin($event, item)"
      ></i>
    </h4>
    <template v-if="!hasUnresolvedAlert">
      <div v-if="offlineState.show" class="mc-control-tower__info mc-control-tower__info--offline">
        <div>
          <i :class="`mc-fa ${offlineState.icon}`"></i>
          <span class="text-muted">{{ offlineState.message }}</span>
        </div>
      </div>
      <div v-else class="mc-control-tower__info">
        <ul>
          <li v-if="!hasPlacards" class="mc-control-tower__info mc-control-tower__info--offline">
            <div>{{ $t('global.text.noSensor') }}</div>
          </li>

          <template v-else>
            <li
              v-for="(placard, index) in item.placards"
              :key="index"
              class="mc-control-tower__placard-list"
            >
              <template v-if="placard">
                <span>{{ listKeys[placard].label }}</span>

                <span>{{
                  `${
                    listKeys[placard]?.formatter && item[listKeys[placard].key]
                      ? listKeys[placard].formatter(item)
                      : item[listKeys[placard].key] === null
                      ? '-'
                      : listKeys[placard]?.formatter
                      ? listKeys[placard].formatter(item)
                      : item[listKeys[placard].key]
                  } ${listKeys[placard].addition}`
                }}</span>
              </template>
            </li>
          </template>
        </ul>
      </div>
    </template>
    <template v-else>
      <div class="mc-control-tower__info mc-control-tower__info--alert">
        <div>
          <b-alert
            v-for="alert in allUnresolvedAlerts.slice(0, 3)"
            :key="alert?.rawAlertId"
            show
            :variant="getVariant(alert, hasAccess(['e4e-alert-center-urgent:read']))"
          >
            <i class="mc-fa icon-fill_alert-triangle"></i>
            {{
              hasRuleNameScope
                ? alert.alertRule
                  ? alert.alertRule.label
                  : $i18n.locale === 'id'
                  ? alert.alert.descriptionIdn
                  : alert.alert.descriptionEng
                : $i18n.locale === 'id'
                ? alert.alert.descriptionIdn
                : alert.alert.descriptionEng
            }}
          </b-alert>
        </div>
        <span v-if="allUnresolvedAlerts.length > 3" class="text-muted">
          {{ totalAlerts }}
        </span>
      </div>
    </template>
    <template
      v-if="
        hasAccessAll([
          'fleet-task-instant:read',
          'fleet-task-instant:create',
          'fleet-task-instant:update',
          'fleet-task-instant:delete',
        ]) && isShowFleetTask
      "
    >
      <div v-if="fleetTaskData !== null" class="mc-control-tower__fo-number">
        #{{ fleetTaskData.number }}
      </div>
    </template>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import { Helper } from '@/mixin/helper'

export default {
  mixins: [Helper],
  inject: ['isShowFleetTask'],
  props: {
    item: {
      type: Object,
      default: () => {},
    },
    isExpanded: {
      type: Boolean,
      default: false,
    },
    listKeys: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
    }),
    offlineState() {
      const motionStatus = this.item.calculatedMotionStatus
      const vehicleStatus = this.item.vehicle?.status

      if (motionStatus === 'O' && vehicleStatus === 'R') {
        return {
          show: true,
          icon: 'icon-fill_service-tools',
          message: this.$t('controlTower.inService'),
        }
      }

      if (motionStatus === 'O' && vehicleStatus === 'I') {
        return {
          show: true,
          icon: 'icon-fill_signal-disconnect',
          message: this.$t('global.filter.offline'),
        }
      }

      return { show: false }
    },
    hasUrgentAlert() {
      return this.item.controlTowerData?.urgent?.unresolvedAlerts?.length > 0
    },
    hasImportantAlert() {
      return this.item.controlTowerData?.important?.unresolvedAlerts?.length > 0
    },
    hasUnresolvedAlert() {
      return (
        this.item.controlTowerData.urgent?.unresolvedAlerts?.length > 0 ||
        this.item.controlTowerData.important?.unresolvedAlerts?.length > 0
      )
    },
    hasPlacards() {
      return this.item?.placards && this.item.placards.length > 0
    },
    fleetTaskData() {
      if (this.item.controlTowerData?.fleetTaskInstant) {
        return this.item.controlTowerData.fleetTaskInstant
      }

      return null
    },
    allUnresolvedAlerts() {
      if (this.hasUrgentAlert && this.hasAccess(['e4e-alert-center-urgent:read']))
        return (
          this.item.controlTowerData?.urgent?.unresolvedAlerts.concat(
            this.item.controlTowerData?.important?.unresolvedAlerts ?? []
          ) || []
        )
      else return this.item.controlTowerData?.important?.unresolvedAlerts || []
    },
    totalAlerts() {
      const urgentAlerts = this.item.controlTowerData?.urgent?.totalUnresolvedAlerts || 0
      const importantAlerts = this.item.controlTowerData?.important?.totalUnresolvedAlerts || 0
      const total = urgentAlerts + importantAlerts - 3
      return `+ ${total > 99 ? 99 : total} ${this.$t('global.object.othersNotification')}`
    },
    slicingNumber() {
      return this.hasAccessAll([
        'fleet-task-instant:read',
        'fleet-task-instant:create',
        'fleet-task-instant:update',
        'fleet-task-instant:delete',
      ]) &&
        this.isShowFleetTask &&
        this.fleetTaskData !== null
        ? 2
        : 3
    },
    hasRuleNameScope() {
      return this.hasAccess('e4J-alert-rule-name-as-title:read')
    },
  },

  methods: {
    convertColor(data) {
      const status = data.calculatedMotionStatus
      let color = ''

      if (this.hasUrgentAlert) {
        color = 'red'
      } else if (this.hasImportantAlert) {
        color = this.hasAccess(['e4e-alert-center-urgent:read']) ? 'yellow' : 'red'
      } else if (status === 'O' || status === 'U') {
        color = 'gray'
      } else {
        color = 'green'
      }

      return color
    },
    showDetail(item) {
      this.$emit('showDetail', item)
    },
    setPin(event, item) {
      event.stopPropagation()
      this.$emit('setPin', item)
    },
  },
}
</script>
